import { useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { QueryClient, QueryClientProvider, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Container,
  Card,
  Button,
  Box,
  Tabs,
  Tab,
  Typography,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import Iconify from '../../components/iconify';
import BreadCrumb from '../../components/breadcrumb/BreadCrumbNavigation';
import Loading from '../../layouts/loading';
import { LeaveRequestForm, LeaveTable, LeaveFilters, LeaveBalanceCards, LeaveBalanceUpload, LeaveBulkImport, InternLeaveBalances } from './components';
import {
  createLeaveRequest,
  getMyLeaves,
  getAllLeaves,
  cancelLeave,
  updateLeaveStatus,
  updateLeaveRequest,
  getRemainingLeaves,
  exportLeaves,
  getMyLeaveBalance,
} from '../../services/leaveService';
import { isAdmin, isManager, isHR, checkComponentPermission, isBoscCompany } from '../../utils/share';

const queryClient = new QueryClient();

function LeavesContent() {
  const [openFormModal, setOpenFormModal] = useState(false);
  const [editingLeave, setEditingLeave] = useState(null);
  const isAdminUser = isAdmin();
  const isManagerUser = isManager();
  const isHRUser = isHR();
  const isBoscUser = isBoscCompany();
  // For managers/HR, start with "All Leaves" tab (1), otherwise "My Leaves" (0)
  // Admins can now access both tabs, so they start with "My Leaves" (0)
  const [activeTab, setActiveTab] = useState(() => ((isManagerUser || isHRUser) && !isAdminUser ? 1 : 0));
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
  });
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelingLeaveId, setCancelingLeaveId] = useState(null);
  const [editConfirmDialogOpen, setEditConfirmDialogOpen] = useState(false);
  const [pendingEditLeave, setPendingEditLeave] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const user = useSelector((state) => state?.login?.user || null);
  const userId = user?.id;
  const isAdminOrManagerOrHR = isAdminUser || isManagerUser || isHRUser;

  const queryClientInstance = useQueryClient();

  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      if (value === undefined || value === '') {
        delete newFilters[key];
      } else {
        newFilters[key] = value;
      }
      // Reset to first page when filters change
      setPagination((prev) => ({ ...prev, page: 1 }));
      return newFilters;
    });
  };

  const handleResetFilters = () => {
    setFilters({});
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (event, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage + 1 }));
  };

  const handleRowsPerPageChange = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPagination({ page: 1, pageSize: newPageSize });
  };

  // Query for my leaves
  const { data: myLeavesData = { rows: [], count: 0 }, isLoading: isLoadingMyLeaves } = useQuery({
    queryKey: ['myLeaves', filters, pagination],
    queryFn: async () => {
      try {
        const response = await getMyLeaves(filters, pagination);
        return response?.data?.data || { rows: [], count: 0 };
      } catch (error) {
        toast.error('Error fetching your leave requests');
        return { rows: [], count: 0 };
      }
    },
  });

  const myLeaves = myLeavesData.rows || [];
  const myLeavesCount = myLeavesData.count || 0;

  // Query for all leaves (admin/manager only)
  const { data: allLeavesData = { rows: [], count: 0 }, isLoading: isLoadingAllLeaves } = useQuery({
    queryKey: ['allLeaves', filters, pagination],
    queryFn: async () => {
      try {
        const response = await getAllLeaves(filters, pagination);
        return response?.data?.data || { rows: [], count: 0 };
      } catch (error) {
        toast.error('Error fetching all leave requests');
        return { rows: [], count: 0 };
      }
    },
    enabled: isAdminOrManagerOrHR,
  });

  const allLeaves = allLeavesData.rows || [];
  const allLeavesCount = allLeavesData.count || 0;

  // Query for leave balance cards (earned, privileged, taken, balance)
  const { data: leaveBalanceData = null } = useQuery({
    queryKey: ['myLeaveBalance', userId],
    queryFn: async () => {
      try {
        const response = await getMyLeaveBalance();
        return response?.data?.data || null;
      } catch (error) {
        console.error('Error fetching leave balance:', error);
        return null;
      }
    },
    enabled: !!userId,
  });

  // Query for remaining leaves (for current user)
  const { data: remainingLeavesData = { eligible: 0, used: 0, remaining: 0 } } = useQuery({
    queryKey: ['remainingLeaves', userId],
    queryFn: async () => {
      try {
        const response = await getRemainingLeaves();
        return response?.data?.data || { eligible: 0, used: 0, remaining: 0 };
      } catch (error) {
        console.error('Error fetching remaining leaves:', error);
        return { eligible: 0, used: 0, remaining: 0 };
      }
    },
    enabled: !!userId, // Only fetch if userId exists
  });

  // Mutation for creating leave
  const createLeaveMutation = useMutation({
    mutationFn: createLeaveRequest,
    onSuccess: (response) => {
      toast.success(response?.data?.message || 'Leave request submitted successfully');
      setOpenFormModal(false);
      setEditingLeave(null);
      queryClientInstance.invalidateQueries(['myLeaves']);
      if (isAdminOrManagerOrHR) {
        queryClientInstance.invalidateQueries(['allLeaves']);
      }
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Error submitting leave request');
    },
  });

  // Mutation for updating leave
  const updateLeaveMutation = useMutation({
    mutationFn: ({ id, data }) => updateLeaveRequest(id, data),
    onSuccess: (response) => {
      toast.success(response?.data?.message || 'Leave request updated successfully');
      setOpenFormModal(false);
      setEditingLeave(null);
      queryClientInstance.invalidateQueries(['myLeaves']);
      if (isAdminOrManagerOrHR) {
        queryClientInstance.invalidateQueries(['allLeaves']);
      }
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Error updating leave request');
    },
  });

  // Mutation for cancelling leave
  const cancelLeaveMutation = useMutation({
    mutationFn: cancelLeave,
    onSuccess: (response) => {
      toast.success(response?.data?.message || 'Leave request deleted successfully');
      queryClientInstance.invalidateQueries(['myLeaves']);
      if (isAdminOrManagerOrHR) {
        queryClientInstance.invalidateQueries(['allLeaves']);
      }
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Error deleting leave request');
    },
  });

  // Mutation for approving leave
  const approveLeaveMutation = useMutation({
    mutationFn: (leaveId) => updateLeaveStatus(leaveId, { status: 'approved' }),
    onSuccess: (response) => {
      toast.success(response?.data?.message || 'Leave request approved successfully');
      queryClientInstance.invalidateQueries(['allLeaves']);
      queryClientInstance.invalidateQueries(['myLeaves']);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Error approving leave request');
    },
  });

  // Mutation for rejecting leave
  const rejectLeaveMutation = useMutation({
    mutationFn: (leaveId) => updateLeaveStatus(leaveId, { status: 'rejected', rejectionReason: 'Rejected by manager' }),
    onSuccess: (response) => {
      toast.success(response?.data?.message || 'Leave request rejected successfully');
      queryClientInstance.invalidateQueries(['allLeaves']);
      queryClientInstance.invalidateQueries(['myLeaves']);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Error rejecting leave request');
    },
  });

  const handleCreateClick = () => {
    setEditingLeave(null);
    setOpenFormModal(true);
  };

  const handleEditLeave = useCallback(
    (leaveId) => {
      const leaveToEdit =
        activeTab === 0 ? myLeaves.find((l) => l.id === leaveId) : allLeaves.find((l) => l.id === leaveId);
      if (!leaveToEdit) return;
      if (leaveToEdit.status === 'approved') {
        setPendingEditLeave(leaveToEdit);
        setEditConfirmDialogOpen(true);
      } else {
        setEditingLeave(leaveToEdit);
        setOpenFormModal(true);
      }
    },
    [activeTab, myLeaves, allLeaves]
  );

  const handleConfirmEdit = useCallback(() => {
    setEditingLeave(pendingEditLeave);
    setOpenFormModal(true);
    setEditConfirmDialogOpen(false);
    setPendingEditLeave(null);
  }, [pendingEditLeave]);

  const handleCancelEdit = useCallback(() => {
    setEditConfirmDialogOpen(false);
    setPendingEditLeave(null);
  }, []);

  const handleFormModalClose = useCallback(() => {
    setOpenFormModal(false);
    setEditingLeave(null);
  }, []);

  const handleFormSubmit = useCallback(
    async (values) => {
      if (editingLeave) {
        await updateLeaveMutation.mutateAsync({ id: editingLeave.id, data: values });
      } else {
        await createLeaveMutation.mutateAsync(values);
      }
      // Only close modal and clear form on success - this is handled by mutation's onSuccess
      return true; // Return true to indicate success
    },
    [createLeaveMutation, updateLeaveMutation, editingLeave]
  );

  const handleCancelLeave = useCallback((leaveId) => {
    setCancelingLeaveId(leaveId);
    setCancelDialogOpen(true);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    if (cancelingLeaveId) {
      cancelLeaveMutation.mutate(cancelingLeaveId);
      setCancelDialogOpen(false);
      setCancelingLeaveId(null);
    }
  }, [cancelingLeaveId, cancelLeaveMutation]);

  const handleApproveLeave = useCallback(
    async (leaveId) => {
      if (window.confirm('Are you sure you want to approve this leave request?')) {
        approveLeaveMutation.mutate(leaveId);
      }
    },
    [approveLeaveMutation]
  );

  const handleRejectLeave = useCallback(
    async (leaveId) => {
      if (window.confirm('Are you sure you want to reject this leave request?')) {
        rejectLeaveMutation.mutate(leaveId);
      }
    },
    [rejectLeaveMutation]
  );

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleExportClick = async () => {
    try {
      setIsExporting(true);
      // Use current month/year for export
      const currentMonth = new Date().getMonth() + 1;
      const currentYear = new Date().getFullYear();
      // Pass all current filters to the export function
      await exportLeaves(currentMonth, currentYear, filters);
      toast.success('Leaves exported successfully!');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error exporting leaves');
    } finally {
      setIsExporting(false);
    }
  };

  // All users (including admins) can access both tabs (0 = My Leaves, 1 = All Leaves)
  const effectiveTab = activeTab;

  const isLoading = effectiveTab === 0 ? isLoadingMyLeaves : isLoadingAllLeaves;
  const currentLeaves = effectiveTab === 0 ? myLeaves : allLeaves;
  const currentLeavesCount = effectiveTab === 0 ? myLeavesCount : allLeavesCount;

  const breadcrumbs = [
    {
      label: 'Home',
      path: '/',
    },
    {
      label: 'Leaves',
      path: '',
    },
  ];

  return (
    <>
      <Helmet>
        <title> Leaves </title>
      </Helmet>
      <BreadCrumb breadcrumbs={breadcrumbs} />
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Stack spacing={1}>
            {/* {effectiveTab === 0 && (
              <Typography variant="body2" color="text.secondary">
                Remaining Leaves: <strong>{remainingLeavesData.remaining.toFixed(1)}</strong> / {remainingLeavesData.eligible.toFixed(1)} 
                {' '}(Used: {remainingLeavesData.used.toFixed(1)})
              </Typography>
            )} */}
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            {effectiveTab === 1 && isAdminOrManagerOrHR && checkComponentPermission('READ_LEAVE') && (
              <LoadingButton
                variant="outlined"
                startIcon={<Iconify icon="eva:download-fill" />}
                onClick={handleExportClick}
                loading={isExporting}
              >
                Export Excel
              </LoadingButton>
            )}
            {effectiveTab === 0 && checkComponentPermission('CREATE_LEAVE') && (
              <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={handleCreateClick}>
                Request Leave
              </Button>
            )}
          </Stack>
        </Stack>

        {effectiveTab === 0 && <LeaveBalanceCards data={leaveBalanceData} />}

        {isAdminOrManagerOrHR ? (
          <Card sx={{ mb: 3 }}>
            <Tabs value={effectiveTab} onChange={handleTabChange}>
              <Tab label="My Leaves" />
              <Tab label="All Leaves" />
              {(isHRUser || isAdminUser) && <Tab label="Leave Balances" />}
              {isBoscUser && (isHRUser || isAdminUser) && <Tab label="Intern Balances" />}
              {isBoscUser && (isHRUser || isAdminUser) && <Tab label="Bulk Import" />}
            </Tabs>
          </Card>
        ) : (
          // For regular users, show a non-clickable indicator for "My Leaves"
          <Card sx={{ mb: 3 }}>
            <Tabs value={0} sx={{ pointerEvents: 'none' }}>
              <Tab label="My Leaves" />
            </Tabs>
          </Card>
        )}

        {effectiveTab === 4 && isBoscUser && (isHRUser || isAdminUser) ? (
          <Card>
            <Box sx={{ p: 3 }}>
              <LeaveBulkImport />
            </Box>
          </Card>
        ) : effectiveTab === 3 && isBoscUser && (isHRUser || isAdminUser) ? (
          <Card>
            <Box sx={{ p: 3 }}>
              <InternLeaveBalances />
            </Box>
          </Card>
        ) : effectiveTab === 2 && (isHRUser || isAdminUser) ? (
          <Card>
            <Box sx={{ p: 3 }}>
              <LeaveBalanceUpload />
            </Box>
          </Card>
        ) : (
          <>
            <LeaveFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onResetFilters={handleResetFilters}
              showEmployeeFilter={effectiveTab === 1 && isAdminOrManagerOrHR}
            />

            <Loading open={isLoading} />
            <Card>
              <Box sx={{ p: 3 }}>
                <LeaveTable
                  leaves={currentLeaves}
                  onCancelLeave={handleCancelLeave}
                  onEditLeave={handleEditLeave}
                  onApproveLeave={handleApproveLeave}
                  onRejectLeave={handleRejectLeave}
                  showEmployee={effectiveTab === 1}
                  currentUserId={userId}
                  count={currentLeavesCount}
                  page={pagination.page - 1}
                  rowsPerPage={pagination.pageSize}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                />
              </Box>
            </Card>

            {openFormModal && (
              <LeaveRequestForm
                open
                onClose={handleFormModalClose}
                onSubmit={handleFormSubmit}
                leaveData={editingLeave}
                isEdit={!!editingLeave}
              />
            )}

            {/* Edit Approved Leave Confirmation Dialog */}
            <Dialog open={editConfirmDialogOpen} onClose={handleCancelEdit}>
              <DialogTitle>Edit Approved Leave</DialogTitle>
              <DialogContent>
                <Typography>Are you sure you want to update this approved leave request?</Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCancelEdit}>No</Button>
                <LoadingButton variant="contained" color="primary" onClick={handleConfirmEdit}>
                  Yes, Edit
                </LoadingButton>
              </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
              <DialogTitle>Delete Leave Request</DialogTitle>
              <DialogContent>
                <Typography>Are you sure you want to delete this leave request? This action cannot be undone.</Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setCancelDialogOpen(false)}>No</Button>
                <LoadingButton
                  variant="contained"
                  color="error"
                  onClick={handleConfirmCancel}
                  loading={cancelLeaveMutation.isLoading}
                >
                  Yes, Delete
                </LoadingButton>
              </DialogActions>
            </Dialog>
          </>
        )}

      </Container>
    </>
  );
}

export default function LeavesPage() {
  return (
    <QueryClientProvider client={queryClient}>
      <LeavesContent />
    </QueryClientProvider>
  );
}
